import { forwardRef, memo, useCallback, useMemo } from 'react';
import { Group, Rect, Text } from 'react-konva';
import type Konva from 'konva';
import type { Node } from '../../../core/models/scene';
import { asTouchKonvaHandler } from '../../../lib/konvaEventAdapters';
import {
  positionStaffingAccent,
  positionStaffingCardLayout,
  positionStaffingStatusLabel,
  toPositionStaffingCardViewModel,
  visiblePositionStaffingStatusLabel,
} from './positionStaffingCard';
import { usePositionStaffingDragStore } from '../stores/positionStaffingDragStore';
import { usePositionStaffingOptimisticStore } from '../stores/positionStaffingOptimisticStore';

interface PositionStaffingNodeProps {
  node: Node;
  selected: boolean;
  canWrite?: boolean;
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  scenarioStatus?: string;
  viewRuleState?: { opacity: number; visible: boolean };
}

const PositionStaffingNodeInner = forwardRef<Konva.Group, PositionStaffingNodeProps>(({
  node,
  selected,
  canWrite = false,
  onClick,
  scenarioStatus,
  viewRuleState,
}, ref) => {
  const positionIds = useMemo(() => Array.isArray(node.attrs?.positionIds)
    ? node.attrs.positionIds.filter((value): value is string => typeof value === 'string')
    : [node.id], [node.attrs?.positionIds, node.id]);
  const positionIdSet = useMemo(() => new Set(positionIds), [positionIds]);
  const isDropTarget = usePositionStaffingDragStore(useCallback((state) =>
    state.hoveredPositionId !== null && (
      state.hoveredPositionId === node.id || positionIdSet.has(state.hoveredPositionId)
    ), [node.id, positionIdSet]));
  const hoveredValidity = usePositionStaffingDragStore(useCallback((state) =>
    state.hoveredPositionId !== null && (
      state.hoveredPositionId === node.id || positionIdSet.has(state.hoveredPositionId)
    )
      ? state.hoveredValidity
      : null, [node.id, positionIdSet]));
  const interactionLocked = usePositionStaffingDragStore((state) => state.interactionLocked);
  const beginAssignmentDrag = usePositionStaffingDragStore((state) => state.begin);
  const optimistic = usePositionStaffingOptimisticStore((state) => state.operation);
  if (viewRuleState && !viewRuleState.visible) return null;
  const width = node.w ?? node.width ?? 270;
  const height = node.h ?? node.height ?? 136;
  const viewModel = toPositionStaffingCardViewModel(node);
  const staffingVisible = viewModel.occupancyVisibility === 'visible';
  const staffingInteractive = canWrite && staffingVisible && !interactionLocked && !optimistic;
  const layout = positionStaffingCardLayout(
    width,
    height,
    viewModel.visibleOccupants,
    viewModel.vacantCount ?? 0,
  );
  const accent = positionStaffingAccent(viewModel.staffingStatus);
  const successorLabel = viewModel.successorCount === null
    ? null
    : `${viewModel.successorCount} successor${viewModel.successorCount === 1 ? '' : 's'}`;
  const metadata = [viewModel.level, viewModel.department, successorLabel].filter(Boolean).join(' · ');
  const staffingStatusLabel = visiblePositionStaffingStatusLabel(viewModel);
  const mutationStroke = viewModel.mutationState === 'conflict'
    ? '#DC2626'
    : viewModel.mutationState === 'pending'
      ? '#2563EB'
      : selected
        ? '#2563EB'
        : '#CBD5E1';
  const mutationDash = viewModel.mutationState === 'pending' || viewModel.scenario || scenarioStatus && scenarioStatus !== 'unchanged'
    ? [5, 4]
    : undefined;
  const dropStroke = hoveredValidity === 'invalid' ? '#DC2626' : hoveredValidity === 'noop' ? '#F59E0B' : '#7C3AED';
  const isOptimisticTarget = positionIdSet.has(optimistic?.targetPositionId ?? '');
  const isOptimisticSource = positionIdSet.has(optimistic?.sourcePositionId ?? '');
  const effectiveMutationStroke = optimistic?.status === 'conflict' && (isOptimisticTarget || isOptimisticSource)
    ? '#DC2626'
    : (isOptimisticTarget || isOptimisticSource) ? '#2563EB' : mutationStroke;

  return (
    <Group
      ref={ref}
      id={node.id}
      name="shape position-staffing-card"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={false}
      opacity={viewRuleState?.opacity ?? 1}
      onClick={onClick}
      onTap={asTouchKonvaHandler(onClick)}
    >
      <Rect
        width={width}
        height={height}
        fill="#FFFFFF"
        stroke={isDropTarget ? dropStroke : effectiveMutationStroke}
        strokeWidth={isDropTarget || isOptimisticTarget || isOptimisticSource || selected || viewModel.mutationState !== 'idle' ? 2 : 1}
        dash={mutationDash}
        cornerRadius={8}
        perfectDrawEnabled={false}
        shadowForStrokeEnabled={false}
      />
      {staffingVisible && <Rect width={6} height={height} fill={accent} cornerRadius={[8, 0, 0, 8]} listening={false} />}

      <Group id={`position-card-header-${node.id}`} name="position-card-header">
        <Rect {...layout.header} fill="transparent" />
        <Text
          x={16}
          y={10}
          width={staffingVisible ? width - 116 : width - 32}
          height={20}
          text={viewModel.title}
          fontFamily="Inter, system-ui, sans-serif"
          fontSize={14}
          fontStyle="bold"
          fill="#0F172A"
          wrap="none"
          ellipsis
          listening={false}
        />
        <Text
          x={16}
          y={30}
          width={width - 32}
          height={14}
          text={metadata}
          fontFamily="Inter, system-ui, sans-serif"
          fontSize={10}
          fill="#64748B"
          wrap="none"
          ellipsis
          listening={false}
        />
        {staffingStatusLabel && <Text
          x={width - 112}
          y={11}
          width={96}
          text={staffingStatusLabel}
          fontFamily="Inter, system-ui, sans-serif"
          fontSize={10}
          fontStyle="bold"
          fill={accent}
          align="right"
          wrap="none"
          listening={false}
        />}
      </Group>

      <Group id={`position-card-body-${node.id}`} name="position-card-body">
        <Rect {...layout.body} fill={staffingVisible ? '#F8FAFC' : '#FFFFFF'} cornerRadius={[0, 0, 8, 8]} />
        {viewModel.visibleOccupants.map((occupant, index) => {
          const chipLayout = layout.chips[index];
          if (!chipLayout) return null;
          const assignmentLabel = occupant.assignmentType === 'primary' ? '' : occupant.assignmentType;
          const trailingLabel = index === viewModel.visibleOccupants.length - 1 && viewModel.overflowCount > 0
            ? `+${viewModel.overflowCount} more`
            : assignmentLabel;
          return (
            <Group
              key={occupant.assignmentId}
              id={`position-occupant-chip-${occupant.assignmentId}`}
              name="position-occupant-chip"
            >
              <Rect {...chipLayout.chip} fill="#FFFFFF" stroke="#CBD5E1" strokeWidth={1} cornerRadius={5} />
              <Rect
                {...chipLayout.dragHandle}
                id={`position-occupant-drag-handle-${occupant.assignmentId}`}
                name="position-occupant-drag-handle"
                fill="transparent"
                listening={staffingInteractive}
                onMouseDown={(event) => {
                  if (!staffingInteractive) return;
                  event.cancelBubble = true;
                  event.evt.preventDefault();
                  beginAssignmentDrag({
                    kind: 'assignment',
                    assignmentId: occupant.assignmentId,
                    personId: occupant.personId,
                    sourcePositionId: occupant.positionId ?? node.id,
                  }, { x: event.evt.clientX, y: event.evt.clientY }, 'mouse');
                }}
                onTouchStart={(event) => {
                  if (!staffingInteractive) return;
                  event.cancelBubble = true;
                  event.evt.preventDefault();
                  const touch = event.evt.touches[0];
                  if (!touch) return;
                  beginAssignmentDrag({
                    kind: 'assignment', assignmentId: occupant.assignmentId,
                    personId: occupant.personId, sourcePositionId: occupant.positionId ?? node.id,
                  }, { x: touch.clientX, y: touch.clientY }, touch.identifier);
                }}
              />
              <Text
                x={chipLayout.chip.x + 10}
                y={chipLayout.chip.y + 4}
                width={chipLayout.chip.width - (trailingLabel ? 78 : 20)}
                height={20}
                text={occupant.displayLabel}
                fontFamily="Inter, system-ui, sans-serif"
                fontSize={11}
                fill="#0F172A"
                wrap="none"
                ellipsis
                listening={false}
              />
              {trailingLabel && (
                <Text
                  x={chipLayout.chip.x + chipLayout.chip.width - 68}
                  y={chipLayout.chip.y + 4}
                  width={58}
                  height={14}
                  text={trailingLabel.charAt(0).toUpperCase() + trailingLabel.slice(1)}
                  fontFamily="Inter, system-ui, sans-serif"
                  fontSize={9}
                  fill="#475569"
                  align="right"
                  listening={false}
                />
              )}
            </Group>
          );
        })}
        {staffingVisible && layout.vacancy && viewModel.vacantCount !== null && (
          <>
            <Rect
              {...layout.vacancy.chip}
              fill="#FFFBEB"
              stroke="#F59E0B"
              strokeWidth={1}
              dash={[4, 3]}
              cornerRadius={5}
              listening={false}
            />
            <Text
              x={layout.vacancy.chip.x + 10}
              y={layout.vacancy.chip.y + 7}
              width={layout.vacancy.chip.width - 20}
              height={18}
              text={viewModel.vacantCount === 1 ? 'Vacant seat' : `${viewModel.vacantCount} vacant seats`}
              fontFamily="Inter, system-ui, sans-serif"
              fontSize={11}
              fontStyle="italic"
              fill="#92400E"
              align="center"
              listening={false}
            />
          </>
        )}
        {staffingVisible && viewModel.visibleOccupants.length === 0 && !layout.vacancy && (
          <Text
            x={16}
            y={layout.body.y + 22}
            width={width - 32}
            text={positionStaffingStatusLabel(viewModel.staffingStatus)}
            fontFamily="Inter, system-ui, sans-serif"
            fontSize={12}
            fontStyle={viewModel.staffingStatus === 'vacant' ? 'italic' : 'normal'}
            fill="#64748B"
            align="center"
            listening={false}
          />
        )}
      </Group>
    </Group>
  );
});

PositionStaffingNodeInner.displayName = 'PositionStaffingNode';

export const PositionStaffingNode = memo(PositionStaffingNodeInner);
export default PositionStaffingNode;
